import type { AppModule } from '@/config/maintenanceModules';
import { useDeploymentSettingsStore } from '@/stores/deploymentSettings';
import { canUserAccessModuleFully } from '@/utils/moduleAccess';
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';

export { canUserAccessModuleFully } from '@/utils/moduleAccess';

export const HOME_ROUTE: RouteLocationRaw = { name: 'home' };

/** Default list route per module (first entry point for each module). */
export const MODULE_DEFAULT_ROUTES: Record<AppModule, RouteLocationRaw> = {
    'integration-lab': { name: 'integration-lab.list' },
    'ps-customer-server': { name: 'ps-customer-server.list' },
    'ps-estimate': { name: 'ps-estimate.list' },
    'license-tracker': { name: 'license-tracker.list' },
    'site-assessment': { name: 'site-assessment.list' },
    'patient-tile-composer': { name: 'patient-tile-composer.list' },
    'mp1-data-analyzer': { name: 'mp1-data-analyzer.analyzer' },
    'ddi-library': { name: 'ddi-library.list' },
    'master-variable': { name: 'master-variable.list' },
    'data-sync': { name: 'data-sync.list' },
    'gateway-management': { name: 'gateway-management.index' },
    'unit': { name: 'unit.list' },
    'go-live-generator': { name: 'go-live-generator.index' },
    'cables-lookup': { name: 'cables-lookup.index' },
};

/** Order used when picking a fallback landing route. */
export const MODULE_FALLBACK_ORDER: AppModule[] = [
    'integration-lab',
    'site-assessment',
    'ps-customer-server',
    'ps-estimate',
    'license-tracker',
    'patient-tile-composer',
    'mp1-data-analyzer',
    'ddi-library',
    'master-variable',
    'data-sync',
    'gateway-management',
    'unit',
    'go-live-generator',
    'cables-lookup',
];

export function resolveDefaultRouteForUser(
    userType: number,
    email: string | undefined,
    deploymentStore: ReturnType<typeof useDeploymentSettingsStore>
): RouteLocationRaw | null {
    for (const module of MODULE_FALLBACK_ORDER) {
        if (canUserAccessModuleFully(module, userType, email, deploymentStore)) {
            return MODULE_DEFAULT_ROUTES[module];
        }
    }
    return null;
}

function routeTargetName(route: RouteLocationRaw): string | undefined {
    return typeof route === 'object' && route !== null && 'name' in route
        ? (route.name as string | undefined)
        : undefined;
}

function redirectToHomeOrAllow(
    to: RouteLocationNormalized,
    next: (location?: RouteLocationRaw | false) => void
): void {
    if (to.name === 'home') {
        next();
        return;
    }
    next(HOME_ROUTE);
}

/** Redirect to the first module the user can access; avoids infinite loops. */
export function redirectToAccessibleRoute(
    to: RouteLocationNormalized,
    next: (location?: RouteLocationRaw | false) => void,
    userType: number,
    email: string | undefined,
    deploymentStore: ReturnType<typeof useDeploymentSettingsStore>
): void {
    const fallback = resolveDefaultRouteForUser(userType, email, deploymentStore);
    const fallbackName = fallback ? routeTargetName(fallback) : undefined;

    if (!fallback || !fallbackName) {
        redirectToHomeOrAllow(to, next);
        return;
    }

    if (to.name === fallbackName) {
        redirectToHomeOrAllow(to, next);
        return;
    }

    next(fallback);
}

/** Post-login landing route for all users. */
export function resolvePostLoginRoute(): RouteLocationRaw {
    return HOME_ROUTE;
}
